import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { MIXPANEL_ACCESS_METHOD } from "@src/constants/analyticsTracking";
import { consumeDirectEntry, isDirectEntry } from "@src/utils/lessonAccess";

/**
 * OP-3133: the Mixpanel `access_method` for a share-link page.
 *
 * Returns "prettyURL" only while the current pathname is the one this page load
 * landed on. The moment the pathname changes (another lesson opened inside the
 * app while the page stays mounted) or the page unmounts, the entry is consumed
 * and the value drops to undefined, so later events report "app".
 *
 * @param enabled false when the current path is not a lesson link (e.g. a bare unit URL).
 */
export const useDirectEntryAccess = (enabled = true): string | undefined => {
  const { pathname } = useLocation();
  const isDirect = enabled && isDirectEntry(pathname);
  const wasDirectRef = useRef(isDirect);

  useEffect(() => {
    if (wasDirectRef.current && !isDirect) {
      consumeDirectEntry();
      wasDirectRef.current = false;
    }
  }, [isDirect]);

  useEffect(
    () => () => {
      if (wasDirectRef.current) {
        consumeDirectEntry();
      }
    },
    [],
  );

  return isDirect ? MIXPANEL_ACCESS_METHOD.PRETTY_URL : undefined;
};
